import { Injectable, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { ExtendedMap } from '@jeppesen-foreflight/jmkw-types';
import { TwcTime } from './twc-time';
import {
  extractForecastTimes,
  getValidTimeForProduct,
  getValidForecastTime,
  isValidTimeForProduct,
  isValidForecastTime
} from './twc-utils/time-validation.utils';

/**
 * Service for managing and validating timestamps in TWC services
 * This centralizes the logic for both WebTile and VectorTile validation
 */
@Injectable({
  providedIn: 'root'
})
export class TwcTimeValidationService {
  private readonly _logger = inject(NGXLogger);

  // Store time mapping information
  private readonly _productTimeMap = new Map<string, string[]>();
  private readonly _tsToFtsMap = new Map<string, string[]>();

  /**
   * Update the cache of valid times for a product
   */
  updateProductTimes(productId: string, times: string[]): void {
    if (times && times.length > 0) {
      this._productTimeMap.set(productId, [...times]);
    }
  }

  /**
   * Update valid forecast timestamps for a given timestamp
   */
  updateTsToFtsMap(ts: string | null, timeMap: ExtendedMap<TwcTime>): void {
    if (ts && timeMap && timeMap.size > 0) {
      const times = extractForecastTimes(timeMap);
      this._tsToFtsMap.set(ts, times);
    }
  }

  /**
   * Update from TimeMap directly
   */
  updateFromTimeMap(
    productId: string,
    ts: string | null,
    timeMap: ExtendedMap<TwcTime>
  ): void {
    if (timeMap && timeMap.size > 0) {
      const times = extractForecastTimes(timeMap);

      // Update product times
      this.updateProductTimes(productId, times);

      // Update ts to fts mapping if we have a timestamp
      if (ts) {
        this.updateTsToFtsMap(ts, timeMap);
      }
    }
  }

  /**
   * Get a valid time for a product, returns the original if valid or the first valid time
   */
  getValidTimeForProduct(productId: string, time: string): string {
    if (!time) return '';

    const validTimes = this._productTimeMap.get(productId);
    if (!validTimes || validTimes.length === 0) {
      return time;
    }

    const result = getValidTimeForProduct(time, validTimes);

    if (result !== time) {
      this._logger.trace(
        `Selected time ${time} is not valid for product ${productId}. Using ${result} instead.`
      );
    }

    return result;
  }

  /**
   * Get a valid forecast timestamp for a given timestamp
   */
  getValidForecastTime(
    ts: string | null | undefined,
    fts: string | undefined
  ): string | undefined {
    if (!ts || !fts) return fts;

    const validFtsValues = this._tsToFtsMap.get(ts);
    if (!validFtsValues || validFtsValues.length === 0) {
      return fts;
    }

    const result = getValidForecastTime(ts, fts, validFtsValues);

    if (result !== fts) {
      this._logger.trace(
        `Selected forecast time ${fts} is not valid for timestamp ${ts}. Using ${result} instead.`
      );
    }

    return result;
  }

  /**
   * Check if a time is valid for a product
   */
  isValidTimeForProduct(productId: string, time: string): boolean {
    const validTimes = this._productTimeMap.get(productId);
    return isValidTimeForProduct(time, validTimes || []);
  }

  /**
   * Check if an fts is valid for a ts
   */
  isValidForecastTime(ts: string, fts: string): boolean {
    const validFtsValues = this._tsToFtsMap.get(ts);
    return isValidForecastTime(ts, fts, validFtsValues || []);
  }

  /**
   * Get all valid times for a product
   */
  getValidTimesForProduct(productId: string): string[] {
    return this._productTimeMap.get(productId) || [];
  }

  /**
   * Get all valid forecast times for a timestamp
   */
  getValidForecastTimes(ts: string): string[] {
    return this._tsToFtsMap.get(ts) || [];
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this._productTimeMap.clear();
    this._tsToFtsMap.clear();
    this._logger.trace('Time validation cache cleared');
  }

  /**
   * Get the most recent time for a product
   */
  getMostRecentTimeForProduct(productId: string): string | undefined {
    const times = this._productTimeMap.get(productId);
    if (!times || times.length === 0) return undefined;

    // Sort times in descending order (newest first)
    const sortedTimes = [...times].sort((a, b) => b.localeCompare(a));
    return sortedTimes[0];
  }

  /**
   * Dump cache contents for debugging
   */
  dumpCacheForDebugging(): Record<string, any> {
    return {
      productTimeMap: Object.fromEntries(this._productTimeMap),
      tsToFtsMap: Object.fromEntries(this._tsToFtsMap)
    };
  }
}
